import React, { useCallback, useEffect, useRef, useState } from "react";
import "./fashionLock.css";

export const PIN_LENGTH = 4;

// The till's lock PIN, read the same way Core's validateUserPin does. null when
// none is set up, in which case the screen offers a plain Unlock for the
// signed-in cashier.
export function configuredLockPin(posConfig, tillData) {
  const pin = posConfig?.lockScreenPin || tillData?.tillAccess?.cwrTill?.lockScreenPin;
  return pin ? String(pin) : null;
}

const KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "clear", "0", "del"];

// Lock till (v4 board "lock-proposed"): covers the till screen until the
// cashier unlocks it. The open bill stays as it is underneath. The lock itself
// never signs anyone out; "Switch cashier" does, through the till's own logout
// (close-till or sign-in), after the cashier confirms it here.
export default function FashionLockScreen({ till, user, initials, lockedAt, billText, pin, onUnlock, onSwitchCashier, switchBlocked, switching }) {
  const [entered, setEntered] = useState("");
  const [error, setError] = useState("");
  const [confirmSwitch, setConfirmSwitch] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    rootRef.current?.focus();
  }, []);

  const press = useCallback(
    (key) => {
      if (!pin) return;
      setError("");
      if (key === "clear") return setEntered("");
      if (key === "del") return setEntered((v) => v.slice(0, -1));
      const next = (entered + key).slice(0, PIN_LENGTH);
      if (next.length < PIN_LENGTH) return setEntered(next);
      setEntered("");
      if (next === pin) onUnlock();
      else setError("That PIN is not right. Try again.");
    },
    [pin, onUnlock, entered]
  );

  // Keys stay on the lock: the till's own shortcuts (F2, Alt+M, scans, Core's
  // keys) listen on window and must not act on the screen underneath.
  const onKeyDown = (e) => {
    if (e.key !== "Tab") e.stopPropagation();
    if (!pin) return;
    if (/^[0-9]$/.test(e.key)) press(e.key);
    else if (e.key === "Backspace") press("del");
    else if (e.key === "Escape") press("clear");
    else return;
    e.preventDefault();
  };

  return (
    <div className="fp-lock" role="dialog" aria-modal="true" aria-label={`${till || "Till"} is locked`} tabIndex={-1} ref={rootRef} onKeyDown={onKeyDown}>
      <div className="fp-lock-l">
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
          <rect x="5" y="11" width="14" height="10" rx="2" />
          <path d="M8 11V7a4 4 0 0 1 8 0v4" />
        </svg>
        <h1>{till || "This till"} is locked</h1>
        <p>
          Locked by {user || "the cashier"}
          {lockedAt ? ` at ${lockedAt}` : ""}. {billText}
        </p>
      </div>
      <div className="fp-lock-pn">
        <span className="fp-lock-av" aria-hidden="true">
          {initials || "?"}
        </span>
        {pin ? (
          <>
            <div className="fp-lock-who">Enter PIN for {user || "the cashier"}</div>
            <div className="fp-lock-dots" aria-label={`${entered.length} of ${PIN_LENGTH} digits entered`} role="status">
              {Array.from({ length: PIN_LENGTH }, (_, i) => (
                <span key={i} className={i < entered.length ? "is-on" : ""} />
              ))}
            </div>
            <p className="fp-lock-err" role="alert">
              {error}
            </p>
            <div className="fp-lock-keys">
              {KEYS.map((k) => (
                <button key={k} type="button" className="fp-lock-k" onClick={() => press(k)} aria-label={k === "del" ? "Delete" : k === "clear" ? "Clear" : k}>
                  {k === "clear" ? (
                    "Clear"
                  ) : k === "del" ? (
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                      <path d="M21 4H8l-7 8 7 8h13a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2z" />
                      <path d="M18 9l-6 6M12 9l6 6" />
                    </svg>
                  ) : (
                    k
                  )}
                </button>
              ))}
            </div>
          </>
        ) : (
          <>
            <div className="fp-lock-who">Signed in as {user || "the cashier"}</div>
            <p className="fp-lock-note">No lock PIN is set for this till.</p>
            <button type="button" className="fp-btn-primary fp-lock-go" onClick={onUnlock}>
              Unlock
            </button>
          </>
        )}
        {onSwitchCashier && (
          <div className="fp-lock-sw">
            {confirmSwitch ? (
              <div className="fp-lock-sw-ask" role="group" aria-label="Switch cashier">
                <p>Sign {user || "the cashier"} out so another cashier can sign in?</p>
                <div className="fp-lock-sw-btns">
                  <button type="button" className="fp-btn-ghost" onClick={() => setConfirmSwitch(false)} disabled={switching}>
                    Cancel
                  </button>
                  <button type="button" className="fp-btn-primary" onClick={onSwitchCashier} disabled={switching}>
                    {switching ? "Signing out…" : "Sign out"}
                  </button>
                </div>
              </div>
            ) : (
              <>
                <button type="button" className="fp-btn-ghost fp-lock-go" onClick={() => setConfirmSwitch(true)} disabled={Boolean(switchBlocked)} aria-describedby={switchBlocked ? "fp-lock-sw-why" : undefined}>
                  Switch cashier
                </button>
                {switchBlocked && (
                  <p className="fp-lock-note" id="fp-lock-sw-why">
                    {switchBlocked}
                  </p>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
